import React, { useState, useEffect, useCallback } from 'react';
import { LanguageContext, type LanguageContextType } from './languageContextValue';
import { useTranslation } from 'react-i18next';

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);

  const availableLanguages = [
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇺🇸' }
  ];

  const setLanguage = useCallback(
    (language: string) => {
      i18n.changeLanguage(language);
      setCurrentLanguage(language);
      localStorage.setItem('selectedLanguage', language);
    },
    [i18n]
  );

  // Gespeicherte Sprache einmalig wiederherstellen.
  //
  // Verglichen wird gegen `i18n.language`, NICHT gegen den State `currentLanguage`. Beides ist
  // hier dasselbe (der State wird aus `i18n.language` initialisiert), aber `i18n.language` ist
  // nicht reaktiv — dadurch bleiben die Abhängigkeiten `[i18n, setLanguage]` stabil und der
  // Effekt läuft genau einmal, wie bisher mit `[]`. Mit `currentLanguage` in den Deps liefe er
  // bei JEDEM Sprachwechsel erneut und läse dabei den localStorage-Wert zurück.
  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage && savedLanguage !== i18n.language) {
      setLanguage(savedLanguage);
    }
  }, [i18n, setLanguage]);

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    availableLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
